import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

/** HTTP statuses we do not report (reduces noise from auth / missing resources). */
const SKIP_HTTP_STATUSES = new Set([401, 404]);

function sentryReady(): boolean {
  return typeof Sentry.getClient === 'function' && Sentry.getClient() != null;
}

function shouldReportStatus(status: number | undefined): boolean {
  if (status == null || Number.isNaN(status)) return true;
  return !SKIP_HTTP_STATUSES.has(status);
}

/**
 * Axios failed responses and network errors — reported to Sentry with request context.
 */
export function captureAxiosError(error: unknown): void {
  if (!sentryReady()) return;

  const ax = error as AxiosError;
  const status = ax.response?.status;

  if (!shouldReportStatus(status)) return;

  Sentry.withScope(scope => {
    scope.setTag('errorSource', 'axios');
    scope.setTag('api', 'axios');
    if (ax.config?.url) {
      scope.setExtra('url', ax.config.baseURL ? `${ax.config.baseURL}${ax.config.url}` : ax.config.url);
    }
    if (ax.config?.method) scope.setExtra('method', String(ax.config.method).toUpperCase());
    if (status != null) scope.setExtra('status', status);
    const data = ax.response?.data;
    if (data !== undefined) {
      scope.setExtra('responseData', typeof data === 'object' ? data : String(data).slice(0, 2000));
    }
    if (ax.code) scope.setExtra('code', ax.code);

    const msg =
      status != null
        ? `Axios HTTP ${status} ${String(ax.config?.method || '').toUpperCase()} ${ax.config?.url || ''}`
        : `Axios ${ax.message || 'request failed'}`;

    const err =
      ax.isAxiosError && ax.response ? new Error(msg) : error instanceof Error ? error : new Error(String(error));
    scope.setExtra('originalError', error);
    Sentry.captureException(err);
  });
}

function rtkArgsToString(args: string | FetchArgs): { url: string; method?: string } {
  if (typeof args === 'string') return { url: args };
  return { url: args.url, method: args.method };
}

/**
 * RTK Query / fetchBaseQuery failures.
 */
export function captureRtkQueryError(args: string | FetchArgs, error: FetchBaseQueryError, baseUrl?: string): void {
  if (!sentryReady()) return;

  const status = error.status;
  const statusNum = typeof status === 'number' ? status : typeof status === 'string' ? parseInt(status, 10) : undefined;

  if (!shouldReportStatus(statusNum)) return;

  const { url, method } = rtkArgsToString(args);
  const fullUrl =
    baseUrl && url && !url.startsWith('http') ? `${baseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}` : url;

  Sentry.withScope(scope => {
    scope.setTag('errorSource', 'rtk-query');
    scope.setTag('api', 'rtk-query');
    scope.setExtra('url', fullUrl);
    if (method) scope.setExtra('method', method);
    scope.setExtra('status', error.status);
    if (error.data !== undefined) {
      scope.setExtra('responseData', typeof error.data === 'object' ? error.data : String(error.data).slice(0, 2000));
    }

    const label = `RTK Query ${String(error.status)} ${method || 'GET'} ${fullUrl}`;
    scope.setExtra('originalError', error);
    Sentry.captureException(new Error(label));
  });
}
